// Utility functions are isolated here for testability.
// Importing ContentFactoryPage or other component files into tests pulls in
// heavy dependencies (Redux, API clients, etc.) that cause test failures.

import moment from "moment-timezone"

import { Status, Health } from "./types"

export type Tab = "profiles" | "posts"

// Landing view on entry. Any post batch (generating, done, or a not-yet-acknowledged
// failure) means the posts tab; otherwise any profile (ready or generating) means the
// profiles tab; an empty account starts on search (which has no tab).
export const computeInitialView = (
  { hasBatch, hasProfiles }: { hasBatch: boolean; hasProfiles: boolean },
): { page: "search" } | { page: "result"; tab: Tab } => {
  if (hasBatch) return { page: "result", tab: "posts" }
  if (hasProfiles) return { page: "result", tab: "profiles" }
  return { page: "search" }
}

type FormatDateOptions = {
  fallback?: string
}

const formatDate = (
  isoString: string | null | undefined,
  timezone: string,
  options: FormatDateOptions = {},
): string => {
  const { fallback = "Unknown date" } = options
  if (!isoString) return fallback
  return moment.tz(isoString, timezone).format("MMM D, YYYY [·] HH:mm")
}

export const formatDateForDisplay = (isoString: string | null | undefined, timezone: string): string =>
  formatDate(isoString, timezone)

export const formatScheduledDate = (isoString: string | null | undefined, timezone: string): string =>
  formatDate(isoString, timezone, { fallback: "Not scheduled" })

export type SourcePage = { url: string; publishedAt: string | null; modifiedAt: string | null }

// Returns undefined for anything that isn't an http(s) URL, so it's never rendered as a link.
export const shortenSourceUrl = (rawUrl: string): string | undefined => {
  let url: URL
  try {
    url = new URL(rawUrl)
  } catch {
    return undefined
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return undefined

  const host = url.hostname.replace(/^www\./, "")
  const segments = url.pathname.split("/").filter(Boolean)
  if (segments.length === 0) return host
  if (segments.length === 1) return `${host}/${segments[0]}`
  return `${host}/…/${segments[segments.length - 1]}`
}

export const formatSourcePageDate = (page: SourcePage, timezone: string): string | undefined => {
  const pub = page.publishedAt ? moment.tz(page.publishedAt, timezone).format("MMM D, YYYY") : null
  const mod = page.modifiedAt ? moment.tz(page.modifiedAt, timezone).format("MMM D, YYYY") : null

  if (pub && mod && pub !== mod) return `Updated ${mod}`
  if (pub) return `Published ${pub}`
  if (mod) return `Updated ${mod}`
  return undefined
}

export const getBatchDisplayName = (name?: string | null): string =>
  name && name.trim().length > 0 ? name : "Unknown source"

export const formatFileSize = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`

export const isGenerating = (batch: { status?: Status; health?: Health }): boolean => {
  if (batch.status === "done") return false
  if (batch.health !== "healthy") return false
  return true
}

// Post row utilities

export const truncateText = (text: string | null | undefined, maxLength: number): string => {
  if (!text) return ""
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength).trimEnd() + "..."
}

export type PostDisplayStatus = "draft" | "scheduled" | "partially_published" | "published"

export type ScheduledPostRef = {
  id: string
  channelId: string
  status: string
  sourceId: string | null
  scheduledAt: string | null
  groupId: string | null
}

export type DraftRef = {
  id: string
  channelIds: string[]
}

export type PostRefs = {
  drafts: DraftRef[]
  scheduledPosts: ScheduledPostRef[]
}

export type EditTarget = { kind: "draft" | "post"; id: string; channelIds: string[] }

export type EditAction =
  | { kind: "open"; target: EditTarget }
  | { kind: "pick"; targets: EditTarget[] }
  | { kind: "none" }

export const resolveEditAction = (post: PostRefs): EditAction => {
  const editableScheduledPosts = post.scheduledPosts.filter((scheduledPost) => scheduledPost.status !== "SENT") // a published post can no longer be edited
  const targets: EditTarget[] = [
    ...editableScheduledPosts.map((scheduledPost): EditTarget => ({
      kind: "post",
      id: scheduledPost.id,
      channelIds: scheduledPost.channelId ? [scheduledPost.channelId] : [],
    })),
    ...post.drafts.map((draft): EditTarget => ({ kind: "draft", id: draft.id, channelIds: draft.channelIds })),
  ]

  if (targets.length === 0) return { kind: "none" }
  if (targets.length === 1) return { kind: "open", target: targets[0] }

  const sharesOneGroup =
    post.drafts.length === 0 &&
    editableScheduledPosts.every(
      (scheduledPost) => scheduledPost.groupId != null && scheduledPost.groupId === editableScheduledPosts[0].groupId,
    ) // the composer opens the whole group from any member
  return sharesOneGroup ? { kind: "open", target: targets[0] } : { kind: "pick", targets }
}

export const getVisitablePosts = (post: PostRefs): ScheduledPostRef[] =>
  post.scheduledPosts.filter((scheduledPost) => scheduledPost.status === "SENT" && !!scheduledPost.sourceId)

export type PostSeries = { name: string; episodeRank: number; lastEpisodeRank: number }

export const describeEpisode = ({ name, episodeRank, lastEpisodeRank }: PostSeries): string =>
  episodeRank === 0
    ? `Teaser of "${name}", a series of ${lastEpisodeRank} ${lastEpisodeRank === 1 ? "episode" : "episodes"}`
    : `Episode ${episodeRank} of ${lastEpisodeRank} in "${name}"`

// Profile markdown sections

export interface ProfileSection {
  key: string
  body: string
}

// Splits profile markdown into its top-level (## h2) sections — each heading
// becomes a section key and everything up to the next heading is its body.
// Lines before the first heading are ignored. Returns [] when there are no
// headings so callers can fall back to rendering the raw markdown.
export const parseProfileSections = (markdown: string): ProfileSection[] => {
  const sections: ProfileSection[] = []
  let current: ProfileSection | null = null
  for (const line of markdown.split("\n")) {
    const heading = line.match(/^##\s+(.+?)\s*$/)
    if (heading) {
      if (current) sections.push(current)
      current = { key: heading[1], body: "" }
    } else if (current) {
      current.body += line + "\n"
    }
  }
  if (current) sections.push(current)
  return sections.map(section => ({ ...section, body: section.body.trim() }))
}

// Countdown timer

export const formatCountdown = (estimatedCompletionAt: number | null | undefined): string | null => {
  if (!estimatedCompletionAt) return null
  const remainingMs = estimatedCompletionAt - Date.now()
  if (remainingMs <= 0) return null
  const totalSeconds = Math.ceil(remainingMs / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}
